import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Upload, Download } from '@mui/icons-material';
import { 
  parseCrrCsvWithCn61Extra, 
  convertCrrDataToCsv, 
  downloadCrrCsv, 
  generateCrrSampleCsv,
  applyCsvDataToCrr,
  UpuCn61ExtraData,
  convertUpuCn61ExtraDataToInputFormat
} from '../../../../utils/crrCsvUtils';

interface UpuRemunerationGroupDto {
  id: number;
  name: string;
  displayName: string;
  operatorId: number | null;
}

interface UpuCrrTdRate {
  upuRemunerationGroupDto: UpuRemunerationGroupDto;
  formatP: {
    tdKg: string | null;
    tdItem: string | null;
  } | null;
  formatG: {
    tdKg: string | null;
    tdItem: string | null;
  } | null;
  formatE: {
    tdKg: string | null;
    tdItem: string | null;
  } | null;
}

interface UpuCrrData {
  inboundUpuCrrTdRates: UpuCrrTdRate[] | null;
  outboundUpuCrrTdRates: UpuCrrTdRate[] | null;
}

interface UpuCn61Data {
  inboundUpuCn61TdRatesDto: any | null;
  outboundUpuCn61TdRatesDto: any | null;
  orCreateInboundUpuCn61TdRatesDto: any;
  orCreateOutboundUpuCn61TdRatesDto: any;
}

interface CRRData {
  ppo: string;
  year: number;
  upuCrrData: UpuCrrData;
  upuCn61Data: UpuCn61Data;
}

interface CRRTableProps {
  data: CRRData;
  onDataChange?: (updatedData: CRRData) => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  currentUserPpo?: string;
  onCn61ExtraDataChange?: (cn61ExtraData: UpuCn61ExtraData[]) => void;
}

interface CRRSubTableProps {
  title: string;
  data: UpuCrrTdRate[];
  onDataChange: (index: number, field: 'formatP' | 'formatG' | 'formatE', subField: 'tdKg' | 'tdItem', value: string) => void;
  onBlur: (index: number, field: 'formatP' | 'formatG' | 'formatE', subField: 'tdKg' | 'tdItem', value: string) => void;
}

function CRRSubTable({ title, data, onDataChange, onBlur }: CRRSubTableProps): React.ReactElement {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }} data-testid="crr-sub-table-empty">
        <Typography sx={{ color: '#666', fontStyle: 'italic' }} data-testid="crr-sub-table-empty-text">
          No {title.toLowerCase()} data available
        </Typography>
      </Box>
    );
  }

  // Determine if this is inbound based on title
  const isInbound = title.toLowerCase().includes('inbound');
  
  // Background colors based on direction (matching CN61Table)
  const getBackgroundColor = (isMainRow: boolean) => {
    if (isInbound) {
      return isMainRow ? '#e8f5e8' : '#f0f8f0'; // Green shades for inbound
    } else {
      return isMainRow ? '#ffe8e8' : '#fff0f0'; // Red shades for outbound
    }
  };

  const inputSx = useMemo(() => ({
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: '32px',
      fontSize: '0.875rem'
    }
  }), []);

  const inputPropsDecimal = useMemo(() => ({
    inputMode: 'decimal' as const,
    pattern: '^\\\d*(\\.\\d{0,3})?$',
    placeholder: '0.000'
  } as const), []);

  const handleBlurToThreeDecimals = useCallback((
    index: number,
    field: 'formatP' | 'formatG' | 'formatE',
    subField: 'tdKg' | 'tdItem',
    value: string
  ) => {
    // Pass the value to parent's blur handler which will format it
    onBlur(index, field, subField, value);
  }, [onBlur]);

  const formatDisplayValue = useCallback((val: string | number | null) => {
    if (val === null || val === undefined || val === '') return '';
    // Return as-is for string values (user is typing)
    return String(val);
  }, []);

  // Check if input has exactly 3 decimals (for visual validation)
  const hasValidFormat = useCallback((val: string | number | null) => {
    if (val === null || val === undefined || val === '') return false;
    const strVal = String(val);
    return /^\d+\.\d{3}$/.test(strVal);
  }, []);

  return (
    <Box sx={{ mb: 3 }} data-testid="crr-sub-table-container">
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#063142' }} data-testid="crr-sub-table-title">
        {title}
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }} data-testid="crr-sub-table-container-paper">
        <Table size="small" sx={{ border: '1px solid #e0e0e0' }} data-testid="crr-sub-table">
          <TableHead data-testid="crr-sub-table-head">
            {/* Main headers */}
            <TableRow data-testid="crr-sub-table-header-row">
              <TableCell 
                rowSpan={2} 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  minWidth: 200, 
                  verticalAlign: 'middle' 
                }}
                data-testid="crr-sub-table-title-header"
              >
                {title}
              </TableCell>
              <TableCell 
                colSpan={2} 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center' 
                }}
                data-testid="crr-sub-table-format-p-header"
              >
                Format P
              </TableCell>
              <TableCell 
                colSpan={2} 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center' 
                }}
                data-testid="crr-sub-table-format-g-header"
              >
                Format G
              </TableCell>
              <TableCell 
                colSpan={2} 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center' 
                }}
                data-testid="crr-sub-table-format-e-header"
              >
                Format E
              </TableCell>
            </TableRow>
            {/* Sub headers */}
            <TableRow data-testid="crr-sub-table-sub-header-row">
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
                data-testid="crr-sub-table-format-p-td-kg-header"
              >
                TD/kg
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
                data-testid="crr-sub-table-format-p-td-item-header"
              >
                TD/item
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
                data-testid="crr-sub-table-format-g-td-kg-header"
              >
                TD/kg
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
                data-testid="crr-sub-table-format-g-td-item-header"
              >
                TD/item
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
                data-testid="crr-sub-table-format-e-td-kg-header"
              >
                TD/kg
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
                data-testid="crr-sub-table-format-e-td-item-header"
              >
                TD/item
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-testid="crr-sub-table-body">
            {data.map((item, index) => (
              <TableRow key={`${item.upuRemunerationGroupDto.id}-${index}`} data-testid={`crr-sub-table-row-${index}`}>
                <TableCell 
                  sx={{ 
                    fontWeight: 600, 
                    backgroundColor: getBackgroundColor(true),
                    border: '1px solid #e0e0e0'
                  }}
                  data-testid={`crr-sub-table-remuneration-group-cell-${index}`}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid={`crr-sub-table-remuneration-group-display-name-${index}`}>
                      {item.upuRemunerationGroupDto.displayName}
                    </Typography>
                    {item.upuRemunerationGroupDto.name !== item.upuRemunerationGroupDto.displayName && (
                      <Typography variant="caption" sx={{ color: '#666' }} data-testid={`crr-sub-table-remuneration-group-name-${index}`}>
                        {item.upuRemunerationGroupDto.name}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                {/* Format P columns */}
                <TableCell sx={{ border: '1px solid #e0e0e0' }} data-testid={`crr-sub-table-format-p-td-kg-cell-${index}`}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatP?.tdKg ?? '')}
                    onChange={(e) => onDataChange(index, 'formatP', 'tdKg', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatP', 'tdKg', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatP?.tdKg && !hasValidFormat(item.formatP.tdKg) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatP?.tdKg ? !hasValidFormat(item.formatP.tdKg) : false}
                    data-testid={`crr-sub-table-format-p-td-kg-input-${index}`}
                  />
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }} data-testid={`crr-sub-table-format-p-td-item-cell-${index}`}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatP?.tdItem ?? '')}
                    onChange={(e) => onDataChange(index, 'formatP', 'tdItem', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatP', 'tdItem', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatP?.tdItem && !hasValidFormat(item.formatP.tdItem) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatP?.tdItem ? !hasValidFormat(item.formatP.tdItem) : false}
                    data-testid={`crr-sub-table-format-p-td-item-input-${index}`}
                  />
                </TableCell>
                {/* Format G columns */}
                <TableCell sx={{ border: '1px solid #e0e0e0' }} data-testid={`crr-sub-table-format-g-td-kg-cell-${index}`}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatG?.tdKg ?? '')}
                    onChange={(e) => onDataChange(index, 'formatG', 'tdKg', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatG', 'tdKg', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatG?.tdKg && !hasValidFormat(item.formatG.tdKg) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatG?.tdKg ? !hasValidFormat(item.formatG.tdKg) : false}
                    data-testid={`crr-sub-table-format-g-td-kg-input-${index}`}
                  />
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }} data-testid={`crr-sub-table-format-g-td-item-cell-${index}`}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatG?.tdItem ?? '')}
                    onChange={(e) => onDataChange(index, 'formatG', 'tdItem', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatG', 'tdItem', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatG?.tdItem && !hasValidFormat(item.formatG.tdItem) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatG?.tdItem ? !hasValidFormat(item.formatG.tdItem) : false}
                    data-testid={`crr-sub-table-format-g-td-item-input-${index}`}
                  />
                </TableCell>
                {/* Format E columns */}
                <TableCell sx={{ border: '1px solid #e0e0e0' }} data-testid={`crr-sub-table-format-e-td-kg-cell-${index}`}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatE?.tdKg ?? '')}
                    onChange={(e) => onDataChange(index, 'formatE', 'tdKg', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatE', 'tdKg', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatE?.tdKg && !hasValidFormat(item.formatE.tdKg) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatE?.tdKg ? !hasValidFormat(item.formatE.tdKg) : false}
                    data-testid={`crr-sub-table-format-e-td-kg-input-${index}`}
                  />
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }} data-testid={`crr-sub-table-format-e-td-item-cell-${index}`}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatE?.tdItem ?? '')}
                    onChange={(e) => onDataChange(index, 'formatE', 'tdItem', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatE', 'tdItem', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatE?.tdItem && !hasValidFormat(item.formatE.tdItem) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatE?.tdItem ? !hasValidFormat(item.formatE.tdItem) : false}
                    data-testid={`crr-sub-table-format-e-td-item-input-${index}`}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function CRRTable({ data, onDataChange, onError, onSuccess, currentUserPpo, onCn61ExtraDataChange }: CRRTableProps): React.ReactElement {
  const [localData, setLocalData] = useState<CRRData>(data);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const inboundFileInputRef = React.useRef<HTMLInputElement>(null);
  const outboundFileInputRef = React.useRef<HTMLInputElement>(null);
  // Store CN61 extra data from CSV upload
  const [cn61ExtraData, setCn61ExtraData] = useState<UpuCn61ExtraData[]>([]);

  // Update local data when prop data changes
  React.useEffect(() => {
    if (data) {
      setLocalData(data);
    }
  }, [data]);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }} data-testid="crr-table-empty">
        <Typography sx={{ color: '#666', fontStyle: 'italic' }} data-testid="crr-table-empty-text">
          No CRR data available
        </Typography>
      </Box>
    );
  }



  const { ppo, year, upuCrrData } = localData;
  const hasInboundData = upuCrrData.inboundUpuCrrTdRates !== null && upuCrrData.inboundUpuCrrTdRates.length > 0;
  const hasOutboundData = upuCrrData.outboundUpuCrrTdRates !== null && upuCrrData.outboundUpuCrrTdRates.length > 0;

  if (!hasInboundData && !hasOutboundData) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }} data-testid="crr-table-no-data">
        <Typography sx={{ color: '#666', fontStyle: 'italic' }} data-testid="crr-table-no-data-text">
          No CRR data available
        </Typography>
      </Box>
    );
  }

  const handleDataChange = useCallback((
    direction: 'inbound' | 'outbound',
    index: number,
    field: 'formatP' | 'formatG' | 'formatE',
    subField: 'tdKg' | 'tdItem',
    value: string
  ) => {
    setLocalData((prev) => {
      const newData = { ...prev };
      const ratesKey = direction === 'inbound' ? 'inboundUpuCrrTdRates' : 'outboundUpuCrrTdRates';
      
      if (newData.upuCrrData[ratesKey]) {
        const updatedArray = [...newData.upuCrrData[ratesKey]!];
        const currentItem = updatedArray[index];
        
        // Initialize the format object if it doesn't exist
        if (!currentItem[field]) {
          currentItem[field] = { tdKg: null, tdItem: null };
        }
        
        // Store the value as entered by user (no automatic formatting)
        currentItem[field]![subField] = value === '' ? null : value;
        
        newData.upuCrrData[ratesKey] = updatedArray;
      }
      
      // Notify parent component of changes
      if (onDataChange) {
        onDataChange(newData);
      }
      
      return newData;
    });
  }, [onDataChange]);

  const handleBlur = useCallback((
    direction: 'inbound' | 'outbound',
    index: number,
    field: 'formatP' | 'formatG' | 'formatE',
    subField: 'tdKg' | 'tdItem',
    value: string
  ) => {
    // Enforce up to 3 decimals; coerce to 3-decimal string if valid
    const sanitized = value.trim();
    const isValid = /^\d*(\.\d{0,3})?$/.test(sanitized);
    if (isValid && sanitized !== '') {
      const numericValue = parseFloat(sanitized);
      const formattedValue = isNaN(numericValue) ? '' : numericValue.toFixed(3);
      
      // Update local data with formatted value
      setLocalData((prev) => {
        const newData = { ...prev };
        const ratesKey = direction === 'inbound' ? 'inboundUpuCrrTdRates' : 'outboundUpuCrrTdRates';
        
        if (newData.upuCrrData[ratesKey]) {
          const updatedArray = [...newData.upuCrrData[ratesKey]!];
          const currentItem = updatedArray[index];
          
          // Initialize the format object if it doesn't exist
          if (!currentItem[field]) {
            currentItem[field] = { tdKg: null, tdItem: null };
          }
          
          // Store the formatted value
          currentItem[field]![subField] = formattedValue;
          
          newData.upuCrrData[ratesKey] = updatedArray;
        }
        
        // Immediately notify parent on blur with final formatted value
        if (onDataChange) {
          onDataChange(newData);
        }
        
        return newData;
      });
    }
  }, [onDataChange]);

  // 3-decimal completeness validation
  const threeDecimalsRegex = useMemo(() => /^\d+\.\d{3}$/, []);
  const isValueComplete = useCallback((val: unknown) => {
    if (val === null || val === undefined || val === '') return false; // All fields must be filled
    const strVal = String(val);
    const numVal = parseFloat(strVal);
    if (isNaN(numVal)) return false;
    return threeDecimalsRegex.test(strVal);
  }, [threeDecimalsRegex]);

  const isDirectionComplete = useCallback((directionData: UpuCrrTdRate[] | null) => {
    if (!directionData || directionData.length === 0) {
      console.log('[CRRTable] No data to validate');
      return false; // No data means can't download
    }
    
    let complete = true;
    
    directionData.forEach((item, index) => {
      ['formatP', 'formatG', 'formatE'].forEach((formatKey) => {
        const format = item[formatKey as 'formatP' | 'formatG' | 'formatE'];
        if (format) {
          const tdKgComplete = isValueComplete(format.tdKg);
          const tdItemComplete = isValueComplete(format.tdItem);
          console.log(`[CRRTable] [${index}] ${item.upuRemunerationGroupDto.name}.${formatKey}.tdKg = "${format.tdKg}" -> complete: ${tdKgComplete}`);
          console.log(`[CRRTable] [${index}] ${item.upuRemunerationGroupDto.name}.${formatKey}.tdItem = "${format.tdItem}" -> complete: ${tdItemComplete}`);
          if (!tdKgComplete || !tdItemComplete) complete = false;
        }
      });
    });
    
    console.log(`[CRRTable] All fields complete: ${complete}`);
    
    // All fields must be filled with exactly 3 decimals
    return complete;
  }, [isValueComplete]);

  // Handle CSV file upload for specific direction
  const handleCsvUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>, direction: 'inbound' | 'outbound') => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Clear previous errors
    setUploadErrors([]);

    try {
      const text = await file.text();
      
      // Parse CSV with year validation - also extracts CN61 extra data
      const { crrData: csvData, cn61ExtraData: extraData } = parseCrrCsvWithCn61Extra(text, localData, currentUserPpo, localData.year);
      
      // Filter CRR CSV data to only include the specified direction
      const filteredCsvData = csvData.filter(row => row.direction === direction);
      
      // Filter CN61 extra data by direction
      const filteredCn61ExtraData = extraData.filter(row => row.direction === direction);
      
      if (filteredCsvData.length === 0 && filteredCn61ExtraData.length === 0) {
        const errorMsg = `CSV file contains no valid data rows for ${direction} direction`;
        setUploadErrors([errorMsg]);
        onError?.(errorMsg);
        return;
      }

      // Apply CRR data if present
      if (filteredCsvData.length > 0) {
        const updatedData = applyCsvDataToCrr(filteredCsvData, localData);
        setLocalData(updatedData);
        
        // Notify parent component of changes
        if (onDataChange) {
          onDataChange(updatedData);
        }
      }
      
      // Store and notify about CN61 extra data if present
      if (filteredCn61ExtraData.length > 0) {
        // Merge with existing CN61 extra data (keep other direction's data)
        const otherDirectionData = cn61ExtraData.filter(row => row.direction !== direction);
        const mergedCn61Data = [...otherDirectionData, ...filteredCn61ExtraData];
        setCn61ExtraData(mergedCn61Data);
        onCn61ExtraDataChange?.(mergedCn61Data);
      }
      
      // Clear any previous errors on successful upload
      setUploadErrors([]);
      
      // Build success message
      let successMsg = '';
      if (filteredCsvData.length > 0 && filteredCn61ExtraData.length > 0) {
        successMsg = `Successfully imported ${filteredCsvData.length} CRR rate entries and ${filteredCn61ExtraData.length} CN61 extra fields for ${direction} from CSV`;
      } else if (filteredCsvData.length > 0) {
        successMsg = `Successfully imported ${filteredCsvData.length} rate entries for ${direction} from CSV`;
      } else {
        successMsg = `Successfully imported ${filteredCn61ExtraData.length} CN61 extra fields for ${direction} from CSV`;
      }
      onSuccess?.(successMsg);
      
      // Reset file input
      const fileInputRef = direction === 'inbound' ? inboundFileInputRef : outboundFileInputRef;
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Split multiple errors if they were joined with |||
      const errorList = errorMessage.includes('|||') 
        ? errorMessage.split('|||') 
        : [`Error parsing CSV file: ${errorMessage}`];
      
      setUploadErrors(errorList);
      onError?.(errorList.join('\n'));
    }
  }, [localData, currentUserPpo, onDataChange, onError, onSuccess, cn61ExtraData, onCn61ExtraDataChange]);

  // Handle CSV download for specific direction
  const handleCsvDownload = useCallback((direction: 'inbound' | 'outbound') => {
    try {
      // Guard: block download if any present fields are incomplete
      const targetRates = direction === 'inbound' ? localData.upuCrrData.inboundUpuCrrTdRates : localData.upuCrrData.outboundUpuCrrTdRates;
      console.log(`[CRRTable] Checking ${direction} rates completeness:`, targetRates);
      
      // Check if there's any data at all
      if (!targetRates || targetRates.length === 0) {
        console.log(`[CRRTable] Blocking download - no ${direction} data structure`);
        onError?.(`Cannot download: No ${direction} data available`);
        return;
      }
      
      const isComplete = isDirectionComplete(targetRates);
      console.log(`[CRRTable] ${direction} rates complete:`, isComplete);
      
      if (!isComplete) {
        console.log(`[CRRTable] Blocking download - incomplete or empty rates detected`);
        onError?.('Please fill all TD rates with exactly 3 decimals before downloading');
        return;
      }

      // Create a filtered data structure for the specific direction
      const filteredData = {
        ...localData,
        upuCrrData: {
          ...localData.upuCrrData,
          inboundUpuCrrTdRates: direction === 'inbound' ? localData.upuCrrData.inboundUpuCrrTdRates : null,
          outboundUpuCrrTdRates: direction === 'outbound' ? localData.upuCrrData.outboundUpuCrrTdRates : null
        }
      };
      
      const csvContent = convertCrrDataToCsv(filteredData, currentUserPpo);
      const filename = direction === 'inbound' 
        ? `td_rates_${currentUserPpo}_${localData.ppo}_${localData.year}.csv`
        : `td_rates_${localData.ppo}_${currentUserPpo}_${localData.year}.csv`;
      downloadCrrCsv(csvContent, filename);
      onSuccess?.(`CSV file for ${direction} downloaded successfully`);
    } catch (error) {
      console.error(`[CRRTable] Download error:`, error);
      onError?.(`Error generating CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [localData, currentUserPpo, onSuccess, onError, isDirectionComplete]);

  // Handle sample CSV download
  const handleSampleCsvDownload = useCallback(() => {
    try {
      const sampleCsv = generateCrrSampleCsv(localData.ppo, localData.year.toString());
      const filename = `crr-upu-sample-${localData.ppo}-${localData.year}.csv`;
      downloadCrrCsv(sampleCsv, filename);
      onSuccess?.('Sample CSV file downloaded successfully');
    } catch (error) {
      onError?.(`Error generating sample CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [localData.ppo, localData.year, onSuccess, onError]);

  const renderDirectionTables = (direction: 'inbound' | 'outbound', directionLabel: string) => {
    const ratesKey = direction === 'inbound' ? 'inboundUpuCrrTdRates' : 'outboundUpuCrrTdRates';
    const directionData = upuCrrData[ratesKey];
    
    if (!directionData || directionData.length === 0) return null;

    const fileInputRef = direction === 'inbound' ? inboundFileInputRef : outboundFileInputRef;

    return (
      <Box sx={{ mb: 4 }} data-testid={`crr-table-${direction}-section`}>
        <CRRSubTable
          title={`${directionLabel} CRR Rates`}
          data={directionData}
          onDataChange={(index, field, subField, value) => handleDataChange(direction, index, field, subField, value)}
          onBlur={(index, field, subField, value) => handleBlur(direction, index, field, subField, value)}
        />
        
        {/* Action buttons below the table */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2, mb: 3 }} data-testid={`crr-table-${direction}-actions`}>
          <Button
            variant="outlined"
            onClick={() => handleCsvDownload(direction)}
            sx={{
              fontSize: '12px',
              textTransform: 'none',
              borderColor: '#00ACE7',
              color: '#00ACE7',
              '&:hover': {
                borderColor: '#0095C7',
                backgroundColor: '#E3F7FE'
              }
            }}
            data-testid={`crr-download-${direction}-csv-btn`}
          >
            Download TD Rates
          </Button>
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            sx={{
              fontSize: '12px',
              textTransform: 'none',
              borderColor: '#00ACE7',
              color: '#00ACE7',
              '&:hover': {
                borderColor: '#0095C7',
                backgroundColor: '#E3F7FE'
              }
            }}
            data-testid={`crr-upload-${direction}-csv-btn`}
          >
            Upload TD Rates
          </Button>
        </Box>
        
        {/* Hidden file input for CSV upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={(e) => handleCsvUpload(e, direction)}
          style={{ display: 'none' }}
          data-testid={`crr-csv-file-input-${direction}`}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '900px' }} data-testid="crr-table-container">
      {/* Report Header - matching CN61Table style */}
      <Box sx={{ mb: 3 }} data-testid="crr-table-header">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#063142' }} data-testid="crr-table-title">
              CRR UPU Configuration - {ppo}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }} data-testid="crr-table-year">
              Year: {year}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Upload Errors Display */}
      {uploadErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} data-testid="crr-upload-errors">
          <AlertTitle>Upload Errors</AlertTitle>
          <List dense sx={{ pt: 0 }}>
            {uploadErrors.map((error, index) => (
              <ListItem key={index} sx={{ py: 0 }}>
                <ListItemText primary={error} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {renderDirectionTables('inbound', 'Inbound')}
      {renderDirectionTables('outbound', 'Outbound')}
    </Box>
  );
}

export default CRRTable;
